/* eslint-disable no-script-url */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Fragment, useState, useContext, useCallback } from 'react';
import classnames from 'classnames';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import PropTypes from 'prop-types';
import { Stack, ChoiceGroup, TextField, Link, Label } from '@bingads-webui-react/fabric-control';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { getAccountCurrencyCode } from '@bingads-webui-campaign/hotel-common-upsert';
import { SpanWithComponent, ErrorString, checkShouldShowError, } from '@bingads-webui-campaign-react/wizard-shared-components';
import { Button } from '@bingads-webui-react/primitive';
import PageContext from '@bingads-webui-static-legacy/page-context';
import { getDefaultBudget, validateBudget, getBudgetErrorMsg } from './utility';

export const individualBudgetOptionKeys = {
  true: 'true',
};

const IndividualBudgetEditor = React.memo((props) => {
  const {
    budget,
    setBudget,
    showBudgetMinRecommend,
    curBudgetAmount,
    activity,
  } = props;

  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const accountCurrencyCode = getAccountCurrencyCode(serverContext);
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldShowError = checkShouldShowError(budget);
  const error = getBudgetErrorMsg({ code: validateBudget({ value: budget.amount, serverContext }), i18n, serverContext });

  const individualBudgetOptions = [
    {
      key: individualBudgetOptionKeys.true,
      text: i18n.getString(_TL_('Standard (we will intelligently pick and choose the right time to serve your ads)')),
    },
  ];

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const currencySymbol = i18n.getCurrencySymbol(PageContext.AccountCurrencyCode);
  const applyRecommend = () => {
    if (activity) {
      activity.trace('BCCForBudgetMinimumRecommendation: apply');
    }
    setBudget({ amount: (curBudgetAmount * 0.5).toFixed(2) * 1, isDirty: true });
  };

  if (activity && showBudgetMinRecommend) {
    activity.trace('BCCForBudgetMinimumRecommendation: show');
  }

  return (
    <div className="individual-budget-editor">
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
        <TextField
          value={budget.amount}
          placeholder={i18n.getString(_TL_('Example: {{defaultBudget}}'), { defaultBudget: getDefaultBudget(serverContext) })}
          aria-label={i18n.getString(_TL_('Campaign budget per day by {{item}}'), { item: accountCurrencyCode })}
          styles={{ fieldGroup: { width: 110 } }}
          onChange={(e, value) => {
            setBudget({ amount: value, isDirty: true });
          }}
        />
        <span>{i18n.getString(_TL_('{{item}}'), { item: accountCurrencyCode })}</span>
      </Stack>
      {shouldShowError && error && <ErrorString error={error} />}
      <Stack>
        <Link className="expander" onClick={toggleExpanded}>
          {i18n.getString(_TL_('Daily budget options'))}
        </Link>
        {isExpanded && (
          <ChoiceGroup
            options={individualBudgetOptions}
            defaultSelectedKey={budget.individualOption || individualBudgetOptionKeys.true}
            onChange={(e, option) => setBudget({ individualOption: option.key })}
          />
        )}
      </Stack>
      {showBudgetMinRecommend ?
        <div className="alert alert-info budget-alert">
          <div className="alert-image" />
          <div className="alert-info-inline">
            <span>{i18n.getString(_TL_('To best optimize your campaigns, we recommend a minimum budget of '))}</span>
            <span className="bold">{currencySymbol + (curBudgetAmount * 0.5).toFixed(2)}</span>
            <span>{i18n.getString(_TL_('.'))}</span>
          </div>
          <div className="alert-budget-button">
            <Button
              className="apply-budget"
              onClick={applyRecommend}
            >
              {i18n.getString(_TL_('Apply recommendation'))}
            </Button>
          </div>

        </div> : null}
    </div>
  );
});

IndividualBudgetEditor.propTypes = {
  setBudget: PropTypes.func.isRequired,
  budget: PropTypes.shape({}),
  showBudgetMinRecommend: PropTypes.bool,
  curBudgetAmount: PropTypes.number,
  activity: PropTypes.shape({}).isRequired,
};

IndividualBudgetEditor.defaultProps = {
  budget: {},
  showBudgetMinRecommend: false,
  curBudgetAmount: 0,
};

export const budgetOptionKeys = {
  Individual: 'Individual',
};

const budgetOptions = props => [
  {
    key: budgetOptionKeys.Individual,
    text: props.i18n.getString(_TL_('Spend/Day (avg.)')),
    onRenderField: (params, render) => (
      <Fragment>
        {render(params)}
        {params.checked && <IndividualBudgetEditor {...params} {...props} />}
      </Fragment>
    ),
    styles: {
      root: {
        '& label': {
          marginBottom: '8px',
          fontWeight: 'unset',
        },
      },
    },
  },
];

export const defaultBudget = {
  option: budgetOptionKeys.Individual,
  individualOption: individualBudgetOptionKeys.true,
  amount: '25',
};

export const CampaignBudget = React.memo((props) => {
  const {
    budget,
    setBudget,
  } = props;

  const mySetBudget = useCallback((partialUpdate) => {
    setBudget({
      ...budget,
      ...partialUpdate,
    });
  }, [budget, setBudget]);

  const i18n = useContext(I18nContext);
  const campaingBudgetText = i18n.getString(_TL_('How much would you like to spend?'));
  const labelId = 'campaign-budget-label';
  return (
    <div className={classnames('campaign-budget', 'section')}>
      <Label
        className="campaign-budget-label"
        id={labelId}
      >
        <SpanWithComponent
          SpanText={campaingBudgetText}
          Component={() => (
            <HelpPopup
              topic="pop_BA_CampaignCreate_Budget_SharedBudget"
              aria-label={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: i18n.getString(_TL_('Campaign budget')) })}
            />)}
        />
      </Label>
      <ChoiceGroup
        defaultSelectedKey={budget.option || budgetOptionKeys.Individual}
        onChange={(e, option) => mySetBudget({ option: option.key })}
        options={budgetOptions({ ...props, i18n, setBudget: mySetBudget })}
        ariaLabelledBy={labelId}
      />
    </div>
  );
});

CampaignBudget.propTypes = {
  budget: PropTypes.shape({}),
  setBudget: PropTypes.func.isRequired,
};

CampaignBudget.defaultProps = {
  budget: {},
};
